/**
 * RejectTransferModal - Modal for rejecting client transfer requests
 *
 * Allows barbers to reject transfers with a mandatory reason selection
 * and optional notes.
 */

import { useState } from 'react';
import { X, Users, FileText, AlertTriangle } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { TransferRejectionReason, TRANSFER_REJECTION_REASON_LABELS } from '../../types';
import { toast } from 'react-hot-toast';

interface RejectTransferModalProps {
  queueId: string;
  clientName: string;
  fromBarberName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function RejectTransferModal({
  queueId,
  clientName,
  fromBarberName,
  isOpen,
  onClose,
  onSuccess,
}: RejectTransferModalProps) {
  const [selectedReason, setSelectedReason] = useState<TransferRejectionReason | null>(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reasons: {
    value: TransferRejectionReason;
    label: string;
    icon: string;
    description: string;
  }[] = [
    {
      value: TransferRejectionReason.BUSY_WITH_CLIENTS,
      label: TRANSFER_REJECTION_REASON_LABELS[TransferRejectionReason.BUSY_WITH_CLIENTS],
      icon: '👥',
      description: 'Estás atendiendo otros clientes',
    },
    {
      value: TransferRejectionReason.OUT_OF_WORKING_HOURS,
      label: TRANSFER_REJECTION_REASON_LABELS[TransferRejectionReason.OUT_OF_WORKING_HOURS],
      icon: '🕐',
      description: 'Fuera de tu horario laboral',
    },
    {
      value: TransferRejectionReason.OTHER_REASON,
      label: TRANSFER_REJECTION_REASON_LABELS[TransferRejectionReason.OTHER_REASON],
      icon: '📝',
      description: 'Otra razón no especificada',
    },
  ];

  const handleReject = async () => {
    if (!selectedReason) {
      toast.error('Selecciona un motivo');
      return;
    }

    setIsSubmitting(true);

    try {
      const rejectTransferFn = httpsCallable<
        { queueId: string; reason: TransferRejectionReason; notes?: string },
        { success: boolean; message: string }
      >(functions, 'rejectTransfer');

      const result = await rejectTransferFn({
        queueId,
        reason: selectedReason,
        notes: notes.trim() || undefined,
      });

      if (result.data.success) {
        toast.success('Traspaso rechazado correctamente');
        onSuccess?.();
        onClose();
      } else {
        toast.error(result.data.message || 'Error al rechazar traspaso');
      }
    } catch (error: any) {
      console.error('Error rejecting transfer:', error);
      toast.error(error.message || 'Error al rechazar traspaso');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedReason(null);
      setNotes('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reject-transfer-modal-title"
        className="bg-surface border border-hairline rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden animate-scale-in"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-hairline">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-danger/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-danger" aria-hidden="true" />
            </div>
            <div>
              <h2 id="reject-transfer-modal-title" className="font-sans text-lg font-semibold text-ink">
                Rechazar Solicitud de Traspaso
              </h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Cerrar"
            className="p-2 text-ink-subtle hover:text-ink hover:bg-surface-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Info Card */}
          <div className="mb-6 p-4 bg-surface-2 border border-hairline rounded-lg">
            <p className="text-sm text-ink-muted">
              <strong className="text-ink">{fromBarberName}</strong> quiere
              traspasarte a{' '}
              <strong className="text-ink">{clientName}</strong>
            </p>
          </div>

          {/* Reason Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-3 text-ink">
              Motivo del rechazo <span className="text-danger">*</span>
            </label>
            <div className="space-y-3">
              {reasons.map((reason) => (
                <button
                  key={reason.value}
                  type="button"
                  disabled={isSubmitting}
                  className={`w-full p-4 border rounded-xl text-left transition-colors ${
                    selectedReason === reason.value
                      ? 'border-danger/50 bg-danger/10'
                      : 'border-hairline bg-surface-2 hover:border-hairline-strong'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  onClick={() => setSelectedReason(reason.value)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-semibold mb-1 ${
                          selectedReason === reason.value ? 'text-danger' : 'text-ink'
                        }`}
                      >
                        {reason.label}
                      </div>
                      <div className="text-sm text-ink-muted">
                        {reason.description}
                      </div>
                    </div>
                    {selectedReason === reason.value && (
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-danger flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Optional Notes */}
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2 text-ink flex items-center gap-2">
              <FileText className="w-4 h-4 text-ink-subtle" />
              Notas adicionales (opcional)
            </label>
            <textarea
              className="w-full p-3 border border-hairline rounded-lg bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent transition disabled:opacity-50 disabled:cursor-not-allowed"
              rows={3}
              maxLength={200}
              placeholder="Añade información adicional si lo deseas..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isSubmitting}
            />
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-ink-subtle">
                Máximo 200 caracteres
              </p>
              <p
                className={`text-xs font-medium tabular-nums ${
                  notes.length > 180 ? 'text-warning' : 'text-ink-subtle'
                }`}
              >
                {notes.length}/200
              </p>
            </div>
          </div>

          {/* Warning */}
          {selectedReason && (
            <div className="mb-4 p-3 bg-warning/10 border border-warning/30 rounded-lg">
              <div className="flex gap-2">
                <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-xs text-ink">
                  El barbero <strong>{fromBarberName}</strong> será notificado del rechazo y el
                  cliente volverá a su cola automáticamente.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-hairline bg-surface-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 border border-hairline text-ink rounded-lg font-medium hover:bg-surface-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleReject}
            disabled={!selectedReason || isSubmitting}
            className="flex-1 px-4 py-3 bg-danger text-white rounded-lg font-medium hover:bg-danger/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Rechazando…</span>
              </>
            ) : (
              <>
                <X className="w-5 h-5" />
                <span>Rechazar Traspaso</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
